import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  fetchAllTowns,
  fetchAllChitalishta,
  searchChitalishta,
  setFilters,
  setSort,
  setPagination,
  selectPagination,
  selectFilters,
  selectSort,
  selectItemsPerPage,
  selectAllChitalishta,
  selectChitalishtaLoading,
} from "../../store/slices/chitalishtaSlice";
import ChitalishteFilters from "./ChitalishteFilters";
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Pagination,
  Stack,
  Paper,
  CircularProgress,
  Fade,
  Zoom,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { LocationOn, Groups, ViewModule } from "@mui/icons-material";

const ChitalishteCard = ({ chitalishte, onSelect, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  // Функция за проверка дали lastUpdatedAt е различно от 1 януари 2026
  const isUpdated = () => {
    if (!chitalishte.lastUpdatedAt) return false;

    try {
      const lastUpdateDate = new Date(chitalishte.lastUpdatedAt);
      const referenceDate = new Date("2026-01-01");

      // Сравняваме датите без часовите компоненти
      return (
        lastUpdateDate.getFullYear() !== referenceDate.getFullYear() ||
        lastUpdateDate.getMonth() !== referenceDate.getMonth() ||
        lastUpdateDate.getDate() !== referenceDate.getDate()
      );
    } catch (error) {
      console.error("Error parsing date:", error);
      return false;
    }
  };

  // Определяне на цвета на лявата линия
  const getBorderLeftColor = () => {
    if (isHovered) {
      return isUpdated() ? "success.main" : "primary.main";
    }
    return isUpdated() ? "#4caf50" : "#667eea"; // Зелено за обновено, синьо за непроменено
  };

  return (
    <Zoom in={true} style={{ transitionDelay: `${index * 50}ms` }}>
      <Card
        sx={{
          height: "100%",
          cursor: "pointer",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          background: "white",
          border: "1px solid",
          borderColor: isHovered
            ? isUpdated()
              ? "success.light"
              : "primary.main"
            : "#e9ecef",
          borderLeft: "4px solid",
          borderLeftColor: getBorderLeftColor(),
          position: "relative",
          overflow: "hidden",
          transform: isHovered ? "translateY(-4px)" : "translateY(0)",
          "&:hover": {
            boxShadow: isUpdated()
              ? "0 8px 24px rgba(76, 175, 80, 0.15)"
              : "0 8px 24px rgba(102, 126, 234, 0.15)",
            zIndex: 1,
          },
        }}
        onClick={() => onSelect(chitalishte)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        elevation={isHovered ? 4 : 1}
      >
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          {/* Header с икона и име */}
          <Box
            sx={{ display: "flex", alignItems: "flex-start", mb: 1.5, gap: 1 }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                background: isHovered
                  ? isUpdated()
                    ? "linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)"
                    : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                  : "linear-gradient(135deg, #f5f7fa 0%, #e9ecef 100%)",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                transition: "all 0.3s ease",
              }}
            >
              <Groups
                sx={{
                  color: isHovered
                    ? "white"
                    : isUpdated()
                      ? "success.main"
                      : "primary.main",
                  fontSize: 20,
                  transition: "all 0.3s ease",
                }}
              />
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                component="h3"
                sx={{
                  fontWeight: 700,
                  lineHeight: 1.3,
                  color: isHovered
                    ? isUpdated()
                      ? "success.main"
                      : "primary.main"
                    : "text.primary",
                  transition: "color 0.3s ease",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  fontSize: "0.875rem",
                }}
              >
                {chitalishte.name}
              </Typography>
              {isUpdated() && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "success.main",
                    fontWeight: 600,
                    fontSize: "0.688rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mt: 0.5,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      backgroundColor: "success.main",
                    }}
                  />
                  Обновено на {new Date(chitalishte.lastUpdatedAt).toLocaleDateString("bg-BG", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "2-digit",
                  })}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Локация */}
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
              <LocationOn
                sx={{
                  fontSize: 14,
                  mr: 0.5,
                  color: isHovered
                    ? isUpdated()
                      ? "success.main"
                      : "primary.main"
                    : "text.secondary",
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  color: "text.primary",
                }}
              >
                {chitalishte.town || "Не е посочено"},{" "}
                {chitalishte.municipality || "Не е посочена"}
              </Typography>
            </Box>
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.688rem",
                color: "text.secondary",
                pl: 2.5,
              }}
            >
              {chitalishte.region || "Не е посочена област"}
            </Typography>
          </Box>

          {/* Footer с статус */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pt: 1,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Chip
              label={chitalishte.status || "Действащо"}
              color={chitalishte.status === "Закрито" ? "error" : "success"}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.688rem",
                fontWeight: 600,
                "& .MuiChip-label": {
                  px: 1,
                },
              }}
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                color: isHovered
                  ? isUpdated()
                    ? "success.main"
                    : "primary.main"
                  : "text.secondary",
                transition: "all 0.3s ease",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.688rem",
                  fontWeight: 600,
                  mr: 0.5,
                }}
              >
                Детайли
              </Typography>
              <Box
                sx={{
                  transform: isHovered ? "translateX(4px)" : "translateX(0)",
                  transition: "transform 0.3s ease",
                }}
              >
                →
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Zoom>
  );
};

const ChitalishteList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Селектори от Redux store
  const chitalishta = useSelector(selectAllChitalishta);
  const loading = useSelector(selectChitalishtaLoading);
  const pagination = useSelector(selectPagination);
  const filters = useSelector(selectFilters);
  const sort = useSelector(selectSort);
  const itemsPerPage = useSelector(selectItemsPerPage);

  // Зареждане на градовете при стартиране
  useEffect(() => {
    dispatch(fetchAllTowns());
  }, [dispatch]);

  // Зареждане на запазени филтри от sessionStorage
  useEffect(() => {
    const savedFilters = sessionStorage.getItem("chitalishteFilters");
    if (savedFilters) {
      try {
        const parsedFilters = JSON.parse(savedFilters);
        if (parsedFilters && Object.keys(parsedFilters).length > 0) {
          dispatch(setFilters(parsedFilters));
        }
      } catch (e) {
        console.error("Error parsing saved filters:", e);
      }
    }

    // Почистване при размонтиране
    return () => {
      sessionStorage.removeItem("chitalishteFilters");
    };
  }, [dispatch]);

  // Функция за проверка на активни филтри
  const hasActiveFilters = useCallback(() => {
    return Object.values(filters).some(
      (value) => value !== null && value !== undefined && value !== "",
    );
  }, [filters]);

  // Функция за зареждане на читалища
  const loadChitalishta = useCallback(() => {
    const params = {
      page: pagination.currentPage,
      size: itemsPerPage,
      sortBy: sort.sortBy,
      sortDirection: sort.sortDirection,
    };

    // Добавяне на филтри само ако има стойност
    if (filters.name) params.name = filters.name;
    if (filters.region) params.region = filters.region;
    if (filters.municipality) params.municipality = filters.municipality;
    if (filters.town) params.town = filters.town;

    // console.log("🔄 Loading chitalishta with params:", params);
    // console.log("🎯 Current filters:", filters);
    // console.log("🔍 Has active filters:", hasActiveFilters());

    if (hasActiveFilters()) {
      // console.log("📍 Using SEARCH endpoint");
      dispatch(searchChitalishta(params));
    } else {
      // console.log("📍 Using FETCH ALL endpoint");
      dispatch(fetchAllChitalishta(params));
    }
  }, [
    pagination.currentPage,
    itemsPerPage,
    sort.sortBy,
    sort.sortDirection,
    filters,
    dispatch,
    hasActiveFilters,
  ]);

  // Зареждане на читалища при промяна на параметрите
  useEffect(() => {
    loadChitalishta();
  }, [loadChitalishta]);

  // Запазване на филтрите в sessionStorage
  useEffect(() => {
    if (filters && Object.keys(filters).length > 0) {
      sessionStorage.setItem("chitalishteFilters", JSON.stringify(filters));
    }
  }, [filters]);

  const handleSortChange = (newSortBy) => {
    const newSortDirection =
      sort.sortBy === newSortBy && sort.sortDirection === "asc"
        ? "desc"
        : "asc";

    dispatch(
      setSort({
        sortBy: newSortBy,
        sortDirection: newSortDirection,
      }),
    );
  };

  const handlePageChange = (event, newPage) => {
    const zeroBasedPage = newPage - 1;

    if (zeroBasedPage !== pagination.currentPage) {
      // console.log(
      //   "📄 Changing page from",
      //   pagination.currentPage,
      //   "to",
      //   zeroBasedPage,
      // );
      // console.log("🎯 Filters will be preserved:", filters);

      dispatch(
        setPagination({
          currentPage: zeroBasedPage,
        }),
      );

      // ⚠️ НЕ НУЛИРАМЕ филтрите - те остават в state!

      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    console.log(
      "🔢 Changing items per page from",
      itemsPerPage,
      "to",
      newItemsPerPage,
    );

    dispatch(
      setPagination({
        currentPage: 0,
        size: newItemsPerPage,
      }),
    );
  };

  const handleSelectChitalishte = (chitalishte) => {
    sessionStorage.setItem("chitalishteFilters", JSON.stringify(filters));

    const slug = chitalishte.slug || chitalishte.id;

    if (slug) {
      navigate(`/community-center/${slug}`, {
        state: { fromSearch: true, filters: filters },
      });
    } else {
      console.error(
        "Cannot navigate: no valid slug or ID found for chitalishte",
        chitalishte,
      );
    }
  };

  // Връщане към списъка със запазени филтри
  useEffect(() => {
    if (location.state?.fromSearch && location.state?.filters) {
      dispatch(setFilters(location.state.filters));
    }
  }, [location, dispatch]);

  // Изчисляване на информация за показване
  const displayCount = pagination.totalItems || 0; // ⚠️ Използваме totalItems от pagination
  const startItem =
    displayCount > 0 ? pagination.currentPage * itemsPerPage + 1 : 0;
  const endItem = Math.min(
    (pagination.currentPage + 1) * itemsPerPage,
    displayCount,
  );
  const totalPages = pagination.totalPages || 1; // ⚠️ Използваме totalPages от pagination

  console.log("📊 Display info:", {
    displayCount,
    startItem,
    endItem,
    totalPages,
    currentPage: pagination.currentPage,
    itemsPerPage,
  });

  if (chitalishta.length === 0 && !loading) {
    return (
      <>
        <ChitalishteFilters
          sort={sort}
          handleSortChange={handleSortChange}
          itemsPerPage={itemsPerPage}
          handleItemsPerPageChange={handleItemsPerPageChange}
        />
        <Container sx={{ py: 4 }}>
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h2" sx={{ mb: 2, opacity: 0.5 }}>
              📚
            </Typography>
            <Typography
              variant="h4"
              color="text.secondary"
              sx={{ mb: 2, fontWeight: 300 }}
            >
              Няма намерени читалища
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Променете критериите за търсене или се свържете с администратор
            </Typography>
          </Box>
        </Container>
      </>
    );
  }

  return (
    <>
      <ChitalishteFilters
        sort={sort}
        handleSortChange={handleSortChange}
        itemsPerPage={itemsPerPage}
        handleItemsPerPageChange={handleItemsPerPageChange}
      />

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Информационна лента */}
        <Box
          sx={{
            mb: 2,
            pb: 1,
            borderBottom: "2px solid #e9ecef",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <ViewModule sx={{ color: "primary.main", fontSize: 18 }} />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: "text.primary",
                fontSize: "0.875rem",
              }}
            >
              {displayCount > 0
                ? `${startItem}-${endItem} от ${displayCount} резултата`
                : "Няма резултати"}
            </Typography>
            {loading && (
              <>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ mx: 1, height: 20 }}
                />
                <CircularProgress size={14} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.75rem" }}
                >
                  Зареждане...
                </Typography>
              </>
            )}
          </Box>
          {displayCount > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.75rem" }}
            >
              {sort.sortDirection === "asc" ? "↑" : "↓"}{" "}
              {getSortLabel(sort.sortBy)}
            </Typography>
          )}
        </Box>

        {/* Grid с картички */}
        <Fade in={!loading} timeout={800}>
          <Grid container spacing={2}>
            {chitalishta.map((chitalishte, index) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
                key={chitalishte.id || `chitalishte-${index}`}
              >
                <ChitalishteCard
                  chitalishte={chitalishte}
                  onSelect={handleSelectChitalishte}
                  index={index}
                />
              </Grid>
            ))}
          </Grid>
        </Fade>

        {/* Пагинация */}
        {displayCount > 0 && totalPages > 1 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 4,
              mb: 2,
            }}
          >
            <Paper
              elevation={1}
              sx={{
                p: 1.5,
                borderRadius: 1,
                border: "1px solid #e9ecef",
                backgroundColor: "white",
              }}
            >
              <Stack spacing={1} alignItems="center">
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.75rem" }}
                >
                  Страница {pagination.currentPage + 1} / {totalPages}
                </Typography>
                <Pagination
                  count={totalPages}
                  page={pagination.currentPage + 1}
                  onChange={handlePageChange}
                  size={isMobile ? "small" : "medium"}
                  color="primary"
                  showFirstButton
                  showLastButton
                  sx={{
                    "& .MuiPaginationItem-root": {
                      borderRadius: 1,
                      fontWeight: 600,
                      fontSize: "0.813rem",
                      minWidth: isMobile ? 28 : 32,
                      height: isMobile ? 28 : 32,
                      "&.Mui-selected": {
                        background:
                          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        color: "white",
                        boxShadow: "0 2px 8px rgba(102, 126, 234, 0.3)",
                      },
                    },
                  }}
                />
              </Stack>
            </Paper>
          </Box>
        )}

        {/* Лоадинг индикатор */}
        {loading && chitalishta.length === 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 8,
            }}
          >
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              Зареждане на читалища...
            </Typography>
          </Box>
        )}
      </Container>
    </>
  );
};

function getSortLabel(sortBy) {
  const labels = {
    name: "име",
    town: "населено място",
    municipality: "община",
    region: "област",
  };
  return labels[sortBy] || sortBy;
}

export default ChitalishteList;
